import { DataDate } from "./data_date";
import { Series, SeriesValue } from "./series";
import { AgeGroupDateUtils } from "./age_group_date_utils";

// エンドポイントから返されるデータの型定義
interface AgeGroupDataPoint {
  data_date: { date: string };
  value: number;
}

interface AgeGroupDataWithColor {
  data: AgeGroupDataPoint[];
  color: string;
}

interface AgeGroupData {
  [key: string]: AgeGroupDataWithColor;
}

// 統計情報の型定義
export interface AgeGroupStatistics {
  increaseRate: number; // 先月までの1年間と1年前の1年間の比較した上昇率（%）
  trend: "急激な上昇がみられます" | "上昇傾向にあります" | "横ばい傾向です" | "減少傾向にあります" | "急激な減少が見られます"; // 直近3年の傾向
  annualSlope: number; // 年率換算の傾き（%）
}

class AgeGroupSeries {
  /**
   * エンドポイントから取得したデータをSeries形式に変換
   * X軸は常に36ヶ月分（先月から遡って3年）に固定し、データがある月だけをプロットする
   */
  static convertToSeries(
    data: AgeGroupData,
    ageGroupName: string,
  ): Series {
    const groupData = data[ageGroupName];
    if (!groupData) {
      return new Series([], ageGroupName);
    }
    const dataPoints = groupData.data || [];

    // 35ヶ月前の月初を計算（X軸の開始点）
    // 先月を含む36ヶ月分を表示するため、先月から35ヶ月前を計算
    const startDate = AgeGroupDateUtils.getThreeYearsAgoStart();

    // データを日付文字列をキーとするマップに変換（高速検索のため）
    const dataMap = new Map<string, number>();
    dataPoints.forEach((point) => {
      // 日付文字列を正規化（YYYY-MM-01形式）
      const date = new Date(point.data_date.date);
      const normalizedDate = AgeGroupDateUtils.formatToYMD(date);
      dataMap.set(normalizedDate, point.value);
    });

    // 36ヶ月分のX軸に沿ってデータをマッピング
    const values: SeriesValue[] = [];
    const workingDate = new Date(startDate);

    for (let i = 0; i < 36; i++) {
      const dateStr = AgeGroupDateUtils.formatToYMD(workingDate);
      const value = dataMap.has(dateStr) ? dataMap.get(dateStr)! : 0;
      values.push([dateStr, value]);

      // 次の月に進む
      workingDate.setMonth(workingDate.getMonth() + 1);
    }

    // X軸の開始点（35ヶ月前の月初）をoriginとして使用
    const origin = DataDate.parseYMD(
      AgeGroupDateUtils.formatToYMD(startDate),
      "monthly"
    ) || undefined;

    return new Series(values, ageGroupName, origin);
  }

  /**
   * 先月と前年の先月を比較した上昇率を計算
   * @param data エンドポイントから取得したデータ
   * @param ageGroupName 年代グループ名
   * @returns 上昇率（%）
   */
  static calculateIncreaseRate(
    data: AgeGroupData,
    ageGroupName: string,
  ): number {
    const groupData = data[ageGroupName];
    if (!groupData) {
      return 0;
    }
    const dataPoints = groupData.data || [];
    if (dataPoints.length === 0) {
      return 0;
    }

    // データを日付順にソート
    const sortedData = [...dataPoints].sort(
      (a, b) =>
        new Date(a.data_date.date).getTime() -
        new Date(b.data_date.date).getTime(),
    );

    // 先月と前年の先月の情報を取得
    const { previousMonth, previousYearPreviousMonth } = AgeGroupDateUtils.getComparisonMonthInfo();

    // 先月のデータを検索
    const previousMonthData = AgeGroupDateUtils.findDataByYearMonth(
      sortedData,
      previousMonth.year,
      previousMonth.month,
    );

    // 前年の先月のデータを検索
    const previousYearPreviousMonthData = AgeGroupDateUtils.findDataByYearMonth(
      sortedData,
      previousYearPreviousMonth.year,
      previousYearPreviousMonth.month,
    );

    // 両方のデータがない場合は0を返す
    if (!previousMonthData || !previousYearPreviousMonthData) {
      return 0;
    }

    const previousMonthValue = previousMonthData.value;
    const previousYearPreviousMonthValue = previousYearPreviousMonthData.value;

    // 上昇率を計算
    if (previousYearPreviousMonthValue === 0) {
      return previousMonthValue > 0 ? 100 : 0;
    }

    return (
      ((previousMonthValue - previousYearPreviousMonthValue) / previousYearPreviousMonthValue) * 100
    );
  }

  /**
   * 直近3年の傾向を判定（年率換算で判定）
   * @param data エンドポイントから取得したデータ
   * @param ageGroupName 年代グループ名
   * @returns トレンド文字列と年率換算の傾き
   */
  static calculateTrend(
    data: AgeGroupData,
    ageGroupName: string,
  ): {
    trend: "急激な上昇がみられます" | "上昇傾向にあります" | "横ばい傾向です" | "減少傾向にあります" | "急激な減少が見られます";
    annualSlope: number;
  } {
    const groupData = data[ageGroupName];
    if (!groupData) {
      return { trend: "横ばい傾向です", annualSlope: 0 };
    }
    const dataPoints = groupData.data || [];
    if (dataPoints.length < 2) {
      return { trend: "横ばい傾向です", annualSlope: 0 };
    }

    // データを日付順にソート
    const sortedData = [...dataPoints].sort(
      (a, b) =>
        new Date(a.data_date.date).getTime() -
        new Date(b.data_date.date).getTime(),
    );

    // 先月の月初を計算（X軸の終了点）
    const previousMonthStart = AgeGroupDateUtils.getPreviousMonthStart();

    // 35ヶ月前の月初を計算（X軸の開始点）
    // 先月を含む36ヶ月分を表示するため、先月から35ヶ月前を計算
    const threeYearsAgoStart = AgeGroupDateUtils.getThreeYearsAgoStart();

    // 直近3年のデータを取得（先月を含む）
    const threeYearData = sortedData.filter((point) => {
      const pointDate = new Date(point.data_date.date);
      return AgeGroupDateUtils.isInRange(pointDate, threeYearsAgoStart, previousMonthStart);
    });

    if (threeYearData.length < 2) {
      return { trend: "横ばい傾向です", annualSlope: 0 };
    }

    // 線形回帰で傾向を判定
    const n = threeYearData.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;

    threeYearData.forEach((point, index) => {
      const x = index;
      const y = point.value;
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    const monthlySlope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);

    // 最初の値（基準値）を取得
    const firstValue = threeYearData[0].value;

    // 年率換算の傾きを計算（月次の傾きを12倍し、基準値に対する割合を計算）
    // 月次の傾きは1ヶ月あたりの変化量なので、年率換算は (monthlySlope * 12 / firstValue) * 100
    const annualSlope = firstValue !== 0
      ? (monthlySlope * 12 / firstValue) * 100
      : 0;

    // 傾きの閾値に基づいてトレンドを判定
    if (annualSlope >= 30) {
      return { trend: "急激な上昇がみられます", annualSlope };
    } else if (annualSlope >= 10) {
      return { trend: "上昇傾向にあります", annualSlope };
    } else if (annualSlope > -10) {
      return { trend: "横ばい傾向です", annualSlope };
    } else if (annualSlope >= -30) {
      return { trend: "減少傾向にあります", annualSlope };
    } else {
      return { trend: "急激な減少が見られます", annualSlope };
    }
  }

  /**
   * 統計情報を計算
   */
  static calculateStatistics(
    data: AgeGroupData,
    ageGroupName: string,
  ): AgeGroupStatistics {
    const trendResult = this.calculateTrend(data, ageGroupName);
    return {
      increaseRate: this.calculateIncreaseRate(data, ageGroupName),
      trend: trendResult.trend,
      annualSlope: trendResult.annualSlope,
    };
  }

  /**
   * 線形回帰のパラメータを取得（グラフ描画用）
   * @param data エンドポイントから取得したデータ
   * @param ageGroupName 年代グループ名
   * @returns 線形回帰の切片と傾き、およびデータ範囲（日付文字列）
   */
  static getLinearRegressionParams(
    data: AgeGroupData,
    ageGroupName: string,
  ): {
    intercept: number;
    slope: number;
    startDate: string;
    endDate: string;
  } | null {
    const groupData = data[ageGroupName];
    if (!groupData) {
      return null;
    }
    const dataPoints = groupData.data || [];
    if (dataPoints.length < 2) {
      return null;
    }

    // データを日付順にソート
    const sortedData = [...dataPoints].sort(
      (a, b) =>
        new Date(a.data_date.date).getTime() -
        new Date(b.data_date.date).getTime(),
    );

    // 先月の月初を計算（X軸の終了点）
    const previousMonthStart = AgeGroupDateUtils.getPreviousMonthStart();

    // 35ヶ月前の月初を計算（X軸の開始点）
    // 先月を含む36ヶ月分を表示するため、先月から35ヶ月前を計算
    const threeYearsAgoStart = AgeGroupDateUtils.getThreeYearsAgoStart();

    // 直近3年のデータを取得（先月を含む）
    // Seriesのインデックス（0から35まで）にマッピングするため、36ヶ月分のマップを作成
    const monthIndexMap = new Map<string, number>();
    const workingDate = new Date(threeYearsAgoStart);
    for (let i = 0; i < 36; i++) {
      const dateStr = `${workingDate.getFullYear()}-${String(workingDate.getMonth() + 1).padStart(2, '0')}-01`;
      monthIndexMap.set(dateStr, i);
      workingDate.setMonth(workingDate.getMonth() + 1);
    }

    const threeYearData: Array<{ point: AgeGroupDataPoint; monthIndex: number }> = [];

    sortedData.forEach((point) => {
      const pointDate = new Date(point.data_date.date);
      if (AgeGroupDateUtils.isInRange(pointDate, threeYearsAgoStart, previousMonthStart)) {
        const pointMonthStart = AgeGroupDateUtils.getMonthStart(pointDate);
        const dateStr = AgeGroupDateUtils.formatToYMD(pointMonthStart);
        const monthIndex = monthIndexMap.get(dateStr);
        if (monthIndex !== undefined) {
          threeYearData.push({ point, monthIndex });
        }
      }
    });

    if (threeYearData.length < 2) {
      return null;
    }

    // 線形回帰を計算（Seriesのインデックスを使用）
    const n = threeYearData.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;

    threeYearData.forEach(({ point, monthIndex }) => {
      const x = monthIndex;
      const y = point.value;
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Seriesに含まれる日付形式（YYYY-MM-01）に合わせる
    const startDateStr = AgeGroupDateUtils.formatToYMD(threeYearsAgoStart);
    const endDateStr = AgeGroupDateUtils.formatToYMD(previousMonthStart);

    return {
      intercept,
      slope,
      startDate: startDateStr,
      endDate: endDateStr,
    };
  }
}

export { AgeGroupSeries, AgeGroupData, AgeGroupDataPoint, AgeGroupDataWithColor };

